import React from 'react';
import _ from 'underscore';

import DataConnector from 'connect/DataConnector';
import {Printer} from 'constants/SampleConstants';
import * as LabelPrintingActions from 'label-printing/LabelPrintingActions';
import StorableStore from 'samples/StorableStore';
import SelectDropdown, {closeSelectDropdown} from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface EnhancedProps {
  printers: Array<Printer> | null;
}

export interface PrinterSelectorProps {
  onChange: (printer: Printer) => void;
  printerId: string | null;
  registryId: string;
}

class PrinterSelectorConnector extends DataConnector<PrinterSelectorProps, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    return {printers: this.storableStore().getPrinters(this.props.registryId)};
  }
}

export class PrinterSelector extends React.Component<PrinterSelectorProps & EnhancedProps> {
  dropdown: SelectDropdown | null = null;

  override componentDidMount(): void {
    LabelPrintingActions.fetchPrinters();
    this.changeInitialPrinter();
  }

  override componentDidUpdate(prevProps: PrinterSelectorProps & EnhancedProps, prevState: {}): void {
    if (!prevProps.printers) {
      this.changeInitialPrinter();
    }
  }

  private changeInitialPrinter(): void {
    // Trigger onChange with initial printer if initial printerId is provided
    if (this.props.printers && this.props.printerId) {
      this.props.onChange(_.findWhere(this.props.printers, {id: this.props.printerId})!);
    }
  }

  private getPrinterDropdownOptions(): Array<OptionData> {
    if (!this.props.printers) {
      return [];
    }
    if (this.props.printers.length === 0) {
      return [{name: 'No printers found'}];
    }
    return this.props.printers.map((printer) => ({
      name: printer.name,
      value: printer.id,
    }));
  }

  private handleOnChange = (value: OptionValue): void => {
    if (this.props.printers && value) {
      this.props.onChange(this.props.printers.find((printer) => printer.id === value)!);
    }
    if (this.dropdown) {
      closeSelectDropdown(this.dropdown);
    }
  };

  override render(): JSX.Element {
    const selectedPrinter = _.findWhere(this.props.printers!, {
      id: this.props.printerId,
    });
    return (
      <SelectDropdown
        displayName={selectedPrinter ? selectedPrinter.name : 'Select a printer...'}
        menu={
          <SearchableMenu
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.FULL}
            onSelect={this.handleOnChange}
            optionDatas={this.getPrinterDropdownOptions()}
          />
        }
        ref={(node) => {
          this.dropdown = node;
        }}
      />
    );
  }
}

export default PrinterSelectorConnector.connect(PrinterSelector);
